import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../i18n';
import {
  getUsers,
  detectJiraCommentOperation,
  insertJiraCommentImputation,
  formatApiError,
} from '../services/api';

const DetailRow = ({ label, value, multiline = false }) => (
  <div className="rounded-lg border border-slate-200 bg-slate-50/70 px-3 py-2">
    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
      {label}
    </p>
    {multiline ? (
      <p className="text-sm text-slate-800 whitespace-pre-wrap break-words">
        {value || '—'}
      </p>
    ) : (
      <p className="text-sm text-slate-800 break-words">{value || '—'}</p>
    )}
  </div>
);

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString(getDateLocale());
};

const formatOperationOptionLabel = (op) => {
  if (!op || typeof op !== 'object') return '';
  const lp = (op.production_line_lp || '').trim();
  const act = (op.activity || '').trim();
  if (lp && act) return `${lp} · ${act}`;
  return act || lp || '';
};

async function fetchAllStaffUsers() {
  const all = [];
  let page = 1;
  const pageSize = 100;

  for (;;) {
    const res = await getUsers({}, page, pageSize);
    const rows = Array.isArray(res?.results) ? res.results : [];
    if (!rows.length) break;
    const staff = rows.filter((u) => u.role === 'EMPLOYE' || u.role === 'MANAGER');
    all.push(...staff);
    if (rows.length < pageSize) break;
    page += 1;
    if (page > 100) break;
  }

  const seen = new Set();
  return all.filter((u) => {
    const em = (u.email || '').trim().toLowerCase();
    if (!em || seen.has(em)) return false;
    seen.add(em);
    return true;
  });
}

const JiraCommentSidebarContent = ({ comment, onCommentImputationInserted }) => {
  const { t } = useTranslation(['admin', 'common']);
  const [staffUsers, setStaffUsers] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [detectedEmail, setDetectedEmail] = useState('');
  const [detectedOperation, setDetectedOperation] = useState(null);
  const [autoDetectMessage, setAutoDetectMessage] = useState(null);
  const [autoDetectError, setAutoDetectError] = useState(null);
  const [autoDetectOperationMessage, setAutoDetectOperationMessage] = useState(null);
  const [autoDetectOperationError, setAutoDetectOperationError] = useState(null);
  const [detectingOperation, setDetectingOperation] = useState(false);
  const [inserting, setInserting] = useState(false);
  const [insertSuccessMessage, setInsertSuccessMessage] = useState(null);
  const [insertError, setInsertError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setStaffLoading(true);
        const users = await fetchAllStaffUsers();
        if (!cancelled) setStaffUsers(users);
      } catch (_) {
        if (!cancelled) setStaffUsers([]);
      } finally {
        if (!cancelled) setStaffLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setDetectedEmail(comment?.author?.emailAddress || '');
    setDetectedOperation(null);
    setAutoDetectMessage(null);
    setAutoDetectError(null);
    setAutoDetectOperationMessage(null);
    setAutoDetectOperationError(null);
    setInsertSuccessMessage(null);
    setInsertError(null);
  }, [comment]);

  const authorName = useMemo(
    () => String(comment?.author?.displayName || '').trim(),
    [comment]
  );

  const handleAutoDetectUserEmail = () => {
    setAutoDetectMessage(null);
    setAutoDetectError(null);

    if (staffLoading) {
      setAutoDetectError(t('jira.employeesStillLoading'));
      return;
    }

    if (!authorName) {
      setAutoDetectError(t('jira.noAuthorDisplayName'));
      return;
    }

    const normalize = (v) => String(v || '').replace(/\s+/g, ' ').trim().toLowerCase();
    const target = normalize(authorName);
    const match = staffUsers.find((u) => normalize(`${u.first_name} ${u.last_name}`) === target);

    if (!match?.email) {
      setAutoDetectError(t('jira.noUserFoundByName', { name: authorName }));
      return;
    }

    setDetectedEmail(match.email);
    setAutoDetectMessage(t('jira.detectedEmailFromName', { email: match.email, name: authorName }));
  };

  const handleAutoDetectOperation = () => {
    setAutoDetectOperationMessage(null);
    setAutoDetectOperationError(null);
    setDetectedOperation(null);
    const body = String(comment?.body || '').trim();
    if (!body) {
      setAutoDetectOperationError(t('jira.noCommentBody'));
      return;
    }
    setDetectingOperation(true);
    detectJiraCommentOperation(body)
      .then((res) => {
        if (!res?.operation) {
          setAutoDetectOperationError(
            res?.activity
              ? t('jira.activityNoOperation', { activity: res.activity })
              : t('jira.noRelatedOperation')
          );
          return;
        }
        setDetectedOperation(res.operation);
        setAutoDetectOperationMessage(
          res?.activity
            ? t('jira.detectedOperationFromActivity', { activity: res.activity })
            : t('jira.relatedOperationDetected')
        );
      })
      .catch((err) => {
        setAutoDetectOperationError(
          err?.message || t('jira.detectOperationFailed')
        );
      })
      .finally(() => {
        setDetectingOperation(false);
      });
  };

  const handleInsert = () => {
    if (!detectedEmail || !detectedOperation?.id || inserting) return;

    setInsertError(null);
    setInsertSuccessMessage(null);
    setInserting(true);

    insertJiraCommentImputation({
      comment_id: comment.id,
      assigned_user_email: detectedEmail,
      operation_id: detectedOperation.id,
      comment: String(comment?.body || '').trim(),
    })
      .then((res) => {
        const gcr = res?.gcr ? ` (${res.gcr})` : '';
        setInsertSuccessMessage(t('jira.imputationInserted', { gcr }));
        onCommentImputationInserted?.({
          commentId: comment.id,
          imputationId: res?.imputation_id ?? null,
        });
      })
      .catch((err) => {
        const formatted = formatApiError(err);
        setInsertError(formatted.generalError || t('jira.insertImputationFailed'));
      })
      .finally(() => {
        setInserting(false);
      });
  };

  if (!comment) return null;

  return (
    <div className="flex h-full flex-col">
      <div className="px-4 py-4 border-b border-slate-200 bg-slate-50/60">
        <h4 className="text-base font-semibold text-slate-900">
          {t('jira.comment')}
        </h4>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        <DetailRow label={t('jira.author')} value={comment.author?.displayName} />
        <div className="rounded-lg border border-slate-200 bg-slate-50/70 px-3 py-2">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
            {t('jira.email')}
          </p>
          <p className="text-sm text-slate-800 break-words">{detectedEmail || '—'}</p>
          <div className="mt-2">
            <button
              type="button"
              onClick={handleAutoDetectUserEmail}
              className="w-full py-2 px-3 text-sm font-semibold rounded-lg border border-primary text-primary bg-white hover:bg-primary/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={staffLoading}
              title={t('jira.autoDetectByAuthorTitle')}
            >
              {staffLoading ? t('jira.loadingUsers') : t('jira.autoDetect')}
            </button>
          </div>
          {autoDetectError && (
            <p className="text-xs text-red-600 mt-2" role="alert">
              {autoDetectError}
            </p>
          )}
          {autoDetectMessage && (
            <p className="text-xs text-emerald-700 mt-2" role="status">
              {autoDetectMessage}
            </p>
          )}
        </div>
        <DetailRow label={t('jira.created')} value={formatDateTime(comment.created)} />
        <DetailRow label={t('jira.storedAt')} value={formatDateTime(comment.created_at)} />
        <div className="rounded-lg border border-slate-200 bg-slate-50/70 px-3 py-2">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
            {t('jira.operationDetection')}
          </p>
          <button
            type="button"
            onClick={handleAutoDetectOperation}
            className="w-full py-2 px-3 text-sm font-semibold rounded-lg border border-primary text-primary bg-white hover:bg-primary/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={detectingOperation}
            title={t('jira.autoDetectOperationTitle')}
          >
            {detectingOperation ? t('jira.detectingOperation') : t('jira.autoDetectOperation')}
          </button>
          {detectedOperation && (
            <div className="mt-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                {t('jira.relatedOperation')}
              </p>
              <p className="text-sm text-slate-800 break-words">
                {formatOperationOptionLabel(detectedOperation) || '—'}
              </p>
            </div>
          )}
          {autoDetectOperationError && (
            <p className="text-xs text-red-600 mt-2" role="alert">
              {autoDetectOperationError}
            </p>
          )}
          {autoDetectOperationMessage && (
            <p className="text-xs text-emerald-700 mt-2" role="status">
              {autoDetectOperationMessage}
            </p>
          )}
        </div>
        <DetailRow label={t('jira.body')} value={comment.body} multiline />
        
        <button
          type="button"
          onClick={handleInsert}
          className="w-full mt-2 py-2 px-3 text-sm font-semibold rounded-lg bg-primary text-white hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={!detectedOperation || !detectedEmail || detectingOperation || inserting}
          title={t('jira.insertRequiresDetectTitle')}
        >
          {inserting ? t('jira.inserting') : t('jira.insert')}
        </button>
        {insertError && (
          <p className="text-xs text-red-600" role="alert">
            {insertError}
          </p>
        )}
        {insertSuccessMessage && (
          <p className="text-xs text-emerald-700" role="status">
            {insertSuccessMessage}
          </p>
        )}
      </div>
    </div>
  );
};

export default JiraCommentSidebarContent;
